import * as Hogan from 'hogan.js';
import {Sprite} from 'pixi.js';
import {LinesParams, WaysParams, PayLinesGenerator, PayCascadeGenerator, CascadeParams, PayWaysGenerator, InfoPageEvents} from '@gongxigames/framework/slot/modules/infopage';
import {ReelConstants} from '@gongxigames/framework/slot/utils';
import {InfoModule} from '@gongxigames/framework/slot/modules/info';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI,
    DeviceType,
    DisplayObjectUtils, HTMLMovieClip, inject,
    ObjectBuilder,
    Scroll,
    System
} from '@gongxigames/framework/core';
import {Image as ImageComponent} from '@gongxigames/framework/core/fragments/BuilderX';
import {ImagesMap} from '@gongxigames/framework/slot/services/TemplateUtils';
import {BoardDimension, PayTableConfig} from '@gongxigames/framework/slot/types/InfoPageConfig';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {HtmlSearch} from '@gongxigames/framework/core/fragments/HtmlSearch';

type AnimationsKeyFrames = [string, string[]][];
type ViewResources = [ImagesMap, AnimationsKeyFrames, string];
enum InitializationState {
    notInitialized,
    initialized,
    initializing,
}

export class InfoPage extends InfoModule {
    public static readonly NAME: symbol = Symbol('InfoPage');

    protected _infoPageContainer!: HTMLElement;
    protected _infoPage!: HTMLElement;
    protected _scroll?: Scroll;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    @inject(CoreDI.DisplayObjectUtils)
    protected _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.EnvironmentProperty)
    protected _envProp!: Record<string, any>;

    @inject(CoreDI.Settings)
    protected _settings!: any;

    protected _isOpen = false;
    protected _initialized: InitializationState = InitializationState.notInitialized;

    protected htmlSearch!: HtmlSearch;

    public init(): void {
        super.init();

        this.createPage();

        /* develblock:start */
        this._cli.addCommand('infoPage', {
            show: () => {
                const menuBar = this._cli.getCommand('menuBar');

                // for mobile
                if (menuBar) {
                    if (typeof menuBar !== 'function') {
                        menuBar.showInfoPage();
                    }
                } else {
                    this.onPageClicked();
                }
            },
            close: () => {
                this.close();
                const menuBar = this._cli.getCommand('menuBar');

                if (menuBar && typeof menuBar !== 'function') {
                    menuBar.close();
                }
            }
        });
        /* develblock:end */
    }

    public destroy(): void {
        super.destroy();

        if (this._infoPageContainer && this._infoPageContainer.parentNode) {
            this._infoPageContainer.remove();
        }

        this._scroll?.removeListeners();
    }

    protected setupEvents(): void {
        super.setupEvents();

        strictUiEmitter.on('UI.menu.closed', this.close.bind(this));
        this.on('General.Popups.Add', this.close.bind(this));
        this.on(KeypadControlsEvents.INFO_CLICKED, this.onPageClicked);

        strictUiEmitter.on('UI.menu.infoPageClicked', this.onPageClicked.bind(this));
    }

    protected createPage(): void {
        this._infoPageContainer = document.createElement('div');
        this._infoPageContainer.id = 'infoPageContainer';
        document.body.appendChild(this._infoPageContainer);

        this._infoPage = document.createElement('div');
        this._infoPage.id = 'infoPage';
        this._infoPageContainer.appendChild(this._infoPage);
    }

    protected onResized(): void {
        if (!this._isOpen || !this._initialized) {
            return;
        }

        requestAnimationFrame(() => {
            this.fitToGameDimensions();

            if (this.isPlatform(DeviceType.MOBILE)) {
                this.setContentSize();
            }

            if (this.isPlatform(DeviceType.DESKTOP) && this._scroll) {
                this._scroll.onResize();
            }
        });
    }

    protected async setupView(): Promise<void> {
        this.setCurrentElements();
        this.showSpinner();

        const [images, kayFrames, templateString] = await this.load();

        this.hideSpinner();

        this._infoPageContainer.appendChild(this._infoPage);

        this.insertHtml(templateString, images);
        this.insertAnimations(kayFrames);

        this.isPlatform(DeviceType.DESKTOP) && this.createCustomScroll().then(() => {
            if (this._scroll && this.htmlSearch) {
                this.htmlSearch.scroll = this._scroll;
            }
        });

        this.addEventListeners();
    }

    protected addEventListeners(): void {
        document.querySelector('#infoPage .close')?.addEventListener('click', this.onCLose.bind(this));
    }

    protected async load(): Promise<ViewResources> {
        const templateString = await this._resourceManager.loadHTMLTemplate('infoPage');
        const [images, kayFrames] = await this.processImages(templateString);

        return [images, kayFrames, templateString];
    }

    protected async onPageClicked(): Promise<void> {
        if (this._isOpen) {
            return;
        }

        this._isOpen = true;

        if (this._initialized === InitializationState.initializing) {
            this.showSpinner();

            return;
        } else if (this._initialized === InitializationState.notInitialized) {
            this._initialized = InitializationState.initializing;

            await this.setupView();

            this._initialized = InitializationState.initialized;
        }

        if (this._isOpen) {
            this.openPage();
        }
    }

    protected openPage(): void {
        this.open();
        this.eventManager.emit(InfoPageEvents.OPENED);
        this.updateMultiplierValues();
        this.updatePayValues();
    }

    protected close(): void {
        if (!this._isOpen) {
            return;
        }

        super.close();

        this._isOpen = false;

        this._currentContainer && this.eventManager.emit(InfoPageEvents.CLOSED);
    }

    protected onCLose(): void {
        if (this.isPlatformDesktop()) {
            this.soundPlayer.play('buttonclick');
        }

        this.close();
    }

    protected hideSpinner(): void {
        this._infoPageContainer.innerHTML = '';
    }

    protected showSpinner(): void {
        this._infoPageContainer.innerHTML = this.createSpinner();
        this._infoPageContainer.classList.add('open');
    }

    protected createSpinner(): string {
        const loaderContainer = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.waitingPopup);
        const loadingIcon = loaderContainer.children[0] as Sprite;
        const base64 = this._displayObjectUtils.spriteToBase64(loadingIcon);

        return `<div class="spinnerContainer"><img alt="spinner" src="${base64}" class="spinner"/></div>`;
    }

    protected processImages(templateString: string): Promise<[ImagesMap, AnimationsKeyFrames]> {
        return new Promise(res => {
            const lang = this._settings.get('lang');
            const images = this._templateUtils.createImageMapAsync(templateString, lang);
            const keysFrames = this.processAnimationKeyframes(templateString);

            const {lines, ways, cascade} = this.getPayTablesImagesData();

            if (lines) {
                images.lines = this.generateLinesPayTable();
            }

            if (ways) {
                images.ways = this.generateWaysPayTable();
            }

            if (cascade) {
                images.cascade = this.generateCascadePayTable();
            }

            res([images, keysFrames]);
        });
    }

    protected processAnimationKeyframes(templateString: string): AnimationsKeyFrames {
        const keysFrames: [string, string[]][] = [];
        const animationKeys = this._templateUtils.getAnimationKeys(templateString);

        for (const animation of animationKeys) {
            const config = this._objectBuilder.getDisplayObjectConfigByName('paytableAssets', animation)!;

            if (!config) {
                consoleLogger.error(`There's no "${animation}" sprite in "paytableAssets" sprite`);

                continue;
            }

            const framesPromises = (config.props.children as any as ImageComponent[]).map((child) => {
                return this._displayObjectUtils.textureToBase64(this._loader.getTexture(child.props.src));
            }) as string[];

            keysFrames.push([animation, framesPromises]);
        }

        return keysFrames;
    }

    protected insertHtml(templateString: string, images: Record<string, string>): void {
        const template = Hogan.compile(templateString);
        const showOddsUpfront = this.settings.get('showOddsUpfront');
        const settings = this.settings.getAllProperties();
        const isWaysGame = this.logic.isWaysGame();
        const texts = {...this._languageService.texts};
        const valuesForTexts = {
            betPredictionFormulaText: {wager: this.logic.getWager()},
            betPredictionFormulaTextWays: {wager: this.logic.getWager()}
        };

        for (const [textID, data] of Object.entries(valuesForTexts)) {
            if (this._languageService.get(textID) !== 'undefined' && data !== undefined) {
                texts[textID] = this._languageService.get(textID, data);
            }
        }

        const html = template.render({
            images,
            texts,
            showOddsUpfront,
            isWaysGame,
            settings
        });

        this._infoPage.insertAdjacentHTML('beforeend', html);

        this.attachSearchField()
    }

    protected attachSearchField() {
        if (this._settings.get('searchInfoFeatureEnabled')) {
            const close = this._infoPage.querySelector('.close');
            const topShadow = this._infoPage.querySelector('.top')!;
            this.htmlSearch = new HtmlSearch('#infoPage .content', 'current', this._languageService.get('search'));

            topShadow.classList.add('search-enabled');
            this._infoPage.insertBefore(this.htmlSearch.controls, close);
            this._infoPageContainer.classList.add('search-component');

        }
    }

    protected getPayTablesImagesData(): {lines: boolean; ways: boolean; cascade: boolean;} {
        const config = this._gameConfig.infoPageConfig?.payTables;
        const data = {lines: false, ways: false, cascade: false};

        const baseType = config?.base?.type;
        const bonusType = config?.bonus?.type;

        data.lines = baseType === 'lines' || bonusType === 'lines' || this._logic.isLinesGame();
        data.ways = baseType === 'ways' || bonusType === 'ways';
        data.cascade = baseType === 'cascade' || bonusType === 'cascade';

        return data;
    }

    protected insertAnimations(keysFrames: [string, string[]][]): void {
        keysFrames.forEach(([animation, frames]) => {
            const element = (document.getElementById(`animations.${animation}`) as HTMLImageElement);
            const movieClip = new HTMLMovieClip(element, frames, Number(element.dataset.fps));

            this.updateManager.add(movieClip);
            movieClip.start();
        });
    }

    protected getPayTableBoardDimension(payTableConfig: PayTableConfig<'ways' | 'cascade'>): BoardDimension {
        const columns = payTableConfig!.board.length;
        const rows = Math.max(...payTableConfig!.board.map(el => el.size + el.shift));

        return {rows, columns};
    }

    protected getPayTableConfig<T extends 'ways' | 'cascade'>(type: T): PayTableConfig<'ways' | 'cascade'> | undefined {
        const payTables = this._gameConfig.infoPageConfig!.payTables;

        if (payTables.base?.type === type) {
            return payTables.base;
        } else if (payTables.bonus?.type === type) {
            return payTables.bonus;
        }
    }

    protected generateLinesPayTable(): string {
        const isMobile = !this.isPlatform(DeviceType.DESKTOP);
        const elementsOnRow = isMobile ? 4 : 5;
        const width = isMobile ? 50 : 40;
        const height = isMobile ? 50 : 40;

        return new PayLinesGenerator({
            width,
            height,
            labelDistance: 12,
            lineWidth: 6,
            padding: {x: 20, y: 0},
            font: {
                size: 65,
                family: 'PF DinDisplay Pro'
            },
            scale: this._screenManager.defaultViewportResolution,
            elementsOnRow,
            xSpaceBetweenElements: 200,
            ySpaceBetweenElements: 80,
            colors: {
                symbolCell: '#ffc000',
                emptyCell: '#363636'
            },
            board: this.getLinesBoard(),
            lines: this.parseLines()
        })
            .generatePayLines()
            .toDataURL();
    }

    protected generateWaysPayTable(): string {
        const height = 120;

        return new PayWaysGenerator({
            width: 140,
            height: 120,
            offsetX: 0,
            check: {
                cap: 'round',
                distance: 30,
                size: height * 0.9,
                lineWidth: 25,
                colors: {
                    win: '#00ff00',
                    lose: '#ff0000'
                }
            },
            scale: this._screenManager.defaultViewportResolution,
            lineWidth: 10,
            padding: {x: 400, y: 150},
            xSpaceBetweenElements: 400,
            ySpaceBetweenElements: 0,
            colors: {
                symbolCell: '#ffc000',
                emptyCell: '#363636'
            },
            board: this.getWaysBoard(),
            ways: this.parseWays()
        })
            .generatePayWays()
            .toDataURL();
    }

    protected generateCascadePayTable(): string {
        const height = 120;

        return new PayCascadeGenerator({
            width: 140,
            height,
            offsetX: 0,
            check: {
                cap: 'round',
                distance: 30,
                size: height * 0.9,
                lineWidth: 25,
                colors: {
                    win: '#00ff00',
                    lose: '#ff0000'
                }
            },
            scale: this._screenManager.defaultViewportResolution,
            lineWidth: 10,
            padding: {x: 1300, y: 150},
            xSpaceBetweenElements: 400,
            ySpaceBetweenElements: 0,
            colors: {
                symbolCell: '#ffc000',
                emptyCell: '#363636'
            },
            board: this.getCascadeBoard(),
            ways: this.parseCascade()
        })
            .generatePayWays()
            .toDataURL();
    }

    protected getLinesBoard(): LinesParams['board'] {
        const reelsCount = ReelConstants.REELS_AMOUNT;
        const rowsCount = ReelConstants.ROW_AMOUNT;
        const board: LinesParams['board'] = {reels: [], dimension: {columns: reelsCount, rows: rowsCount}};

        for (let i = 0; i < reelsCount; i++) {
            board.reels.push({shift: 0, size: rowsCount});
        }

        return board;
    }

    protected parseLines(): LinesParams['lines'] {
        const reelsCount = ReelConstants.REELS_AMOUNT;
        const payLines = this.logic.getLines()!;
        const lines: LinesParams['lines'] = [];

        for (const payLine of payLines) {
            const line: number[][] = [];

            for (let reel = 0; reel < reelsCount; reel++) {
                line.push([reel, payLine.getRow(reel)]);
            }

            lines.push(line);
        }

        return lines;
    }

    protected getWaysBoard(): WaysParams['board'] {
        const payTableConfig = this.getPayTableConfig('ways')!;
        const dimension = this.getPayTableBoardDimension(payTableConfig)!;
        const board: WaysParams['board'] = {reels: payTableConfig.board, dimension};

        return board;
    }

    protected parseWays(): WaysParams['ways'] {
        const payTables = this._gameConfig.infoPageConfig!.payTables;
        const ways: WaysParams['ways'] = {win: [], lose: []};

        if (payTables.base?.type === 'ways') {
            ways.win = payTables.base.win;
            ways.lose = payTables.base.lose;
        } else if (payTables.bonus?.type === 'ways') {
            ways.win = payTables.bonus.win;
            ways.lose = payTables.bonus.lose;
        }

        return ways;
    }

    protected getCascadeBoard(): CascadeParams['board'] {
        const payTableConfig = this.getPayTableConfig('cascade')!;
        const dimension = this.getPayTableBoardDimension(payTableConfig)!;
        const board: CascadeParams['board'] = {reels: payTableConfig.board, dimension};

        return board;
    }

    protected parseCascade(): CascadeParams['ways'] {
        const payTables = this._gameConfig.infoPageConfig!.payTables;
        const ways: CascadeParams['ways'] = {win: []};

        if (payTables.base?.type === 'cascade') {
            ways.win = payTables.base.win;
        } else if (payTables.bonus?.type === 'cascade') {
            ways.win = payTables.bonus.win;
        }

        return ways;
    }

    protected updateMultiplierValues(): void {
        const multiplierSignFirst = this.settings.get('multiplierSignFirst');

        if (!multiplierSignFirst) {
            return;
        }

        const pays = document
            .querySelectorAll('.yellow-text li, .gray-text li, span.yellow-text, span.gray-text') as any;

        for (const pay of pays) {
            const valueArr = pay.innerHTML.split('');

            if (valueArr[valueArr.length - 1] === 'x') {
                valueArr.length--;
                pay.innerHTML = 'x' + valueArr.join('');
            }
        }
    }

    protected updatePayValues(): void {
        const pays = document.querySelectorAll('.pays') as any;
        const showOddsUpfront = this.settings.get('showOddsUpfront');

        for (const pay of pays) {
            const [symbolId, symbolsAmount] = pay.classList[1].split('*');
            const betFactor = this.logic.getBetFactor(symbolId);
            let amount = this.logic.getSlotSymbol(symbolId).paytable[symbolsAmount];

            if (!showOddsUpfront) {
                amount *= betFactor;
            } else if (betFactor === this.logic.getTotalBet()) {
                amount *= this.logic.getWager();
            }

            pay.innerHTML = this.formatOddsOrCurrency(amount);
        }
    }

    protected formatOddsOrCurrency(amount: number): string {
        return this.settings.get('showOddsUpfront') ? `${amount}/1` : this._currencyFormatter.formatCents(amount);
    }

    protected createCustomScroll(): Promise<void> {
        return new Promise<void>(resolve => {
            const images: HTMLImageElement[] = document.querySelectorAll('#infoPage img') as any;
            const imagesAmount = images.length;
            let imagesLoaded = 0;

            images.forEach(image => image.onload = () => {
                imagesLoaded++;

                if (imagesLoaded === imagesAmount) {
                    this._scroll = new Scroll(this._infoPage, this._device, {searchEnabled: this.settings.get('searchInfoFeatureEnabled')});
                    resolve();
                }
            });
        });
    }

    public setCurrentElements(): void {
        this._currentContainer = this._infoPageContainer;
        this._currentPage = this._infoPage;
        this._currentScroll = this._scroll;
    }
}
